/**
 * ProductTable - Tabela de produtos usando DataTable genérico
 * Mantém funcionalidade específica: formatação de moeda
 * Princípios: DRY (reutiliza DataTable), SRP (apenas configuração de colunas)
 */
import React, { useMemo } from 'react';
import { ProductDTO } from '../../../services/modules/productService';
import DataTable, { Column } from '../../../components/shared/DataTable';
import ActionMenu, { createEditAction, createDeleteAction, createDuplicateAction } from '../../../components/shared/ActionMenu';

interface ProductTableProps {
    products: ProductDTO[];
    isLoading?: boolean;
    onEdit: (product: ProductDTO) => void;
    onDelete: (product: ProductDTO) => void;
    onDuplicate: (product: ProductDTO) => void;
    showCheckbox?: boolean;
    selectedIds?: Set<string>;
    allSelected?: boolean;
    someSelected?: boolean;
    onToggleSelection?: (id: string) => void;
    onToggleAll?: () => void;
    footer?: React.ReactNode;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const ProductTable: React.FC<ProductTableProps> = ({
    products, isLoading, onEdit, onDelete, onDuplicate,
    showCheckbox = false, selectedIds = new Set(), onToggleSelection, onToggleAll, footer
}) => {
    const columns: Column<ProductDTO>[] = useMemo(() => [
        {
            key: 'name', header: 'Nome',
            render: (product) => <span className="font-medium text-default">{product.name}</span>
        },
        {
            key: 'value', header: 'Valor',
            render: (product) => <span className="text-secondary">{formatCurrency(product.value)}</span>
        },
        {
            key: 'description', header: 'Descrição',
            render: (product) => <span className="text-secondary text-sm">{product.description || '-'}</span>
        },
        {
            key: 'actions', header: 'Ações', align: 'right',
            render: (product) => (
                <ActionMenu actions={[
                    createEditAction(() => onEdit(product)),
                    createDuplicateAction(() => onDuplicate(product)),
                    createDeleteAction(() => onDelete(product))
                ]} />
            )
        }
    ], [onEdit, onDuplicate, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={products}
            getRowKey={(p) => p.id}
            emptyMessage="Nenhum produto encontrado."
            isLoading={isLoading}
            selectable={showCheckbox}
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onToggleAll={onToggleAll}
            footer={footer}
        />
    );
};

export default ProductTable;
